// ============================================================================
// "Not started yet" and "Set start date" — a signed line that waits on the
// client (item 847, Option 1, Ben 2026-10-13).
//
// ⛔ SAME SHAPE AS STOP LINE AND REMOVE LINE. Every change to a line's life has
// its own button and a required reason, and the refusals live in the database
// (mark_service_line_not_started / set_service_line_start). The screen shows the
// database's own sentence; repeating its tests here would be a second opinion
// that can drift.
//
// ⛔ No new status word. A line that has not started is the existing Scheduled
// status with no start date; the signed price stays on the line and the
// building's rate leaves it out until it runs.
// ============================================================================

import { useState, type ReactNode } from 'react';
import { Button, useToast } from '../../../shared/components';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';

function niceDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function Sheet({
  busy,
  onClose,
  labelledBy,
  children,
}: {
  busy: boolean;
  onClose: () => void;
  labelledBy: string;
  children: ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
    >
      <div className="absolute inset-0 bg-tcs-scrim/60" onClick={busy ? undefined : onClose} />
      <div className="relative z-10 w-full max-w-md rounded-t-2xl bg-tcs-bg p-5 shadow-2xl sm:rounded-2xl">
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Not started yet — on a running line.
// ---------------------------------------------------------------------------
export function MarkNotStartedDialog({
  open,
  assignmentId,
  label,
  onClose,
  onDone,
}: {
  open: boolean;
  assignmentId: string;
  label: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function mark() {
    // The demo's own words, and the same sentence the database refuses with.
    if (reason.trim() === '') {
      setError("Say why it hasn't started. It's the only record of what we're waiting on.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { error: rpcErr } = await supabase.rpc('mark_service_line_not_started', {
        p_assignment_id: assignmentId,
        p_reason: reason,
      });
      if (rpcErr) throw rpcErr;
      toast.success('Marked not started. Billing and sub payments stop; the signed price stays on the line.');
      setReason('');
      onDone();
      onClose();
    } catch (e) {
      setError(errorMessage(e, 'Could not mark the line not started.', 'mark-line-not-started'));
    } finally {
      setBusy(false);
    }
  }

  function close() {
    setError(null);
    onClose();
  }

  return (
    <Sheet busy={busy} onClose={close} labelledBy="not-started-title">
      <h2 id="not-started-title" className="text-sm font-semibold text-tcs-text">
        Mark {label} as not started
      </h2>
      <p className="mt-1 text-sm text-tcs-text-secondary">
        Billing and sub payments stop. The signed price stays on the line.
      </p>
      <label className="mt-4 block text-xs font-medium text-tcs-text" htmlFor="not-started-reason">
        Why hasn&rsquo;t it started? (required)
      </label>
      <textarea
        id="not-started-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={busy}
        rows={3}
        autoFocus
        placeholder="e.g. Waiting on client census. Elijah to confirm."
        className="mt-1 w-full rounded-md border border-tcs-border bg-tcs-raised px-2 py-1.5 text-sm text-tcs-text"
      />
      {error && (
        <p
          role="alert"
          className="mt-3 rounded-md border border-st-danger-border bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg-strong"
        >
          {error}
        </p>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => void mark()} disabled={busy}>
          {busy ? 'Saving…' : 'Mark not started'}
        </Button>
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Set start date — on a line that is not started (or booked, once the building
// is open). The date rule and the nightly job decide running vs booked.
// ---------------------------------------------------------------------------
export function SetLineStartDialog({
  open,
  assignmentId,
  label,
  currentStart,
  onClose,
  onDone,
}: {
  open: boolean;
  assignmentId: string;
  label: string;
  /** The booked date, when there is one. */
  currentStart: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [date, setDate] = useState(currentStart ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function save() {
    if (!date) {
      setError('Pick a date.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('set_service_line_start', {
        p_assignment_id: assignmentId,
        p_start_date: date,
      });
      if (rpcErr) throw rpcErr;
      const res = (data ?? {}) as { running?: boolean; finance_told?: boolean };
      // ⛔ Whether Finance was told is the database's answer (what the producer
      // queued), not a guess made here.
      const told = res.finance_told ? ' Finance has been sent the start-billing message.' : '';
      toast.success(
        res.running
          ? `${label} is running from ${niceDate(date)}.${told}`
          : `${label} is booked to start ${niceDate(date)}. It switches on that morning.${told}`,
      );
      onDone();
      onClose();
    } catch (e) {
      setError(errorMessage(e, 'Could not set the start date.', 'set-line-start'));
    } finally {
      setBusy(false);
    }
  }

  function close() {
    setError(null);
    onClose();
  }

  return (
    <Sheet busy={busy} onClose={close} labelledBy="line-start-title">
      <h2 id="line-start-title" className="text-sm font-semibold text-tcs-text">
        Set the {label} start date
      </h2>
      <p className="mt-1 text-sm text-tcs-text-secondary">
        Today or a past date starts the line now. A later date books it, and it switches on the
        morning that date arrives. Finance is told when it starts.
      </p>
      <label className="mt-4 block text-xs font-medium text-tcs-text" htmlFor="line-start-date">
        First day of service
      </label>
      <input
        id="line-start-date"
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        disabled={busy}
        className="mt-1 w-full max-w-[220px] rounded-md border border-tcs-border bg-tcs-raised px-2 py-1.5 text-sm text-tcs-text"
      />
      {error && (
        <p
          role="alert"
          className="mt-3 rounded-md border border-st-danger-border bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg-strong"
        >
          {error}
        </p>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving…' : 'Save start date'}
        </Button>
      </div>
    </Sheet>
  );
}
